// Inboedel field-update reducer — extraction step I2 from InboedelScreen handleCustomFieldChange.
// Verbatim setData-updater body; SCREEN_ID injected as deps.screenId. The pre-setData guards
// (validation-error clears, address/building/gezinssamenstelling lock guards, PCODE/HUISNR
// stale-clears) stay in the screen, as does the post-setData updateSharedAddress mapping.
// Known smell, preserved on purpose: side effects (saveWorkflowData / family-composition sync /
// woonhuis+AVP step-data writes / setValidationErrors / deferred Dekkingen saveStepData) run
// INSIDE the updater. Unlike Woonhuis, Inboedel HAS a generic else path-walker that mutates the
// shallow copy in place — do not harmonize.

import { syncFamilyCompositionToOtherProducts } from '../../../utils/familyComposition';
import type { SaaProductId } from '../../../utils/addressSharing';
import { mapTypeWoning as toWoonhuisTypeWoning } from '../woonhuis/infofolio';

export interface InboedelFieldUpdateDeps {
  risicoAdresGelijk: string;
  selectedProducts: Record<string, boolean | undefined> | undefined;
  getStepData: (id: string) => any;
  saveStepData: (id: string, data: any) => void;
  saveWorkflowData: (stepId: string, displayData: any) => void;
  setIsMonumentProperty: (isMonument: boolean) => void;
  setValidationErrors: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  screenId: SaaProductId;
}

export const applyInboedelFieldUpdate = (
  prevData: any,
  fieldPath: string,
  value: any,
  deps: InboedelFieldUpdateDeps,
): any => {
  const {
    risicoAdresGelijk,
    selectedProducts,
    getStepData,
    saveStepData,
    saveWorkflowData,
    setIsMonumentProperty,
    screenId,
  } = deps;

  let newData = { ...prevData };

  // Handle BA_PCODE - updates BA_PCODE, and VP_PCODE only when J
  if (fieldPath === 'Contractdocument.PP.BA.BA_PCODE') {
    // Spread previous BA but remove stale street/city from previous lookup
    const { BA_STRAAT: _bs, BA_PLAATS: _bp, ...restBA } = prevData.Contractdocument?.PP?.BA || {};
    if (risicoAdresGelijk === 'J') {
      const { VP_STRAAT: _vs, VP_PLAATS: _vp, ...restVP } = prevData.Contractdocument?.PP?.VP || {};
      newData = {
        ...prevData,
        Contractdocument: {
          ...prevData.Contractdocument,
          PP: {
            ...prevData.Contractdocument?.PP,
            BA: { ...restBA, BA_PCODE: value },
            VP: { ...restVP, VP_PCODE: value }
          }
        }
      };
    } else {
      newData = {
        ...prevData,
        Contractdocument: {
          ...prevData.Contractdocument,
          PP: {
            ...prevData.Contractdocument?.PP,
            BA: { ...restBA, BA_PCODE: value }
          }
        }
      };
    }

    // Clear stale premium - address changed so previous calculation is invalid
    if (newData.Contractdocument?.PP?.DA) {
      delete newData.Contractdocument.PP.DA.DA_TTOT;
    }

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_PCODE = value;
        if (risicoAdresGelijk === 'J') {
          woonhuisData.Contractdocument.PP.VP.VP_PCODE = value;
        }
        // Clear stale street/city in synced product
        delete woonhuisData.Contractdocument.PP.BA.BA_STRAAT;
        delete woonhuisData.Contractdocument.PP.BA.BA_PLAATS;
        saveStepData('woonhuis', woonhuisData);
      }
    }

    // Sync to Aansprakelijkheid if it exists
    if (selectedProducts?.aansprakelijkheid) {
      const avpData = getStepData('aansprakelijkheid');
      if (avpData) {
        if (!avpData.Contractdocument) avpData.Contractdocument = {};
        if (!avpData.Contractdocument.PP) avpData.Contractdocument.PP = {};
        if (!avpData.Contractdocument.PP.BA) avpData.Contractdocument.PP.BA = {};
        avpData.Contractdocument.PP.BA.BA_PCODE = value;
        // Clear stale street/city in synced product
        delete avpData.Contractdocument.PP.BA.BA_STRAAT;
        delete avpData.Contractdocument.PP.BA.BA_PLAATS;
        saveStepData('aansprakelijkheid', avpData);
      }
    }

    // Note: Address lookup is now triggered explicitly on blur/enter, not on every field change
    // This prevents premature API calls while user is still typing
  }
  // Handle BA_HUISNR - updates BA_HUISNR, and VP_HUISNR only when J
  else if (fieldPath === 'Contractdocument.PP.BA.BA_HUISNR') {
    // Spread previous BA but remove stale street/city from previous lookup
    const { BA_STRAAT: _bs, BA_PLAATS: _bp, ...restBA } = prevData.Contractdocument?.PP?.BA || {};
    if (risicoAdresGelijk === 'J') {
      const { VP_STRAAT: _vs, VP_PLAATS: _vp, ...restVP } = prevData.Contractdocument?.PP?.VP || {};
      newData = {
        ...prevData,
        Contractdocument: {
          ...prevData.Contractdocument,
          PP: {
            ...prevData.Contractdocument?.PP,
            BA: { ...restBA, BA_HUISNR: value },
            VP: { ...restVP, VP_HUISNR: value }
          }
        }
      };
    } else {
      newData = {
        ...prevData,
        Contractdocument: {
          ...prevData.Contractdocument,
          PP: {
            ...prevData.Contractdocument?.PP,
            BA: { ...restBA, BA_HUISNR: value }
          }
        }
      };
    }

    // Clear stale premium - address changed so previous calculation is invalid
    if (newData.Contractdocument?.PP?.DA) {
      delete newData.Contractdocument.PP.DA.DA_TTOT;
    }

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_HUISNR = value;
        if (risicoAdresGelijk === 'J') {
          woonhuisData.Contractdocument.PP.VP.VP_HUISNR = value;
        }
        // Clear stale street/city in synced product
        delete woonhuisData.Contractdocument.PP.BA.BA_STRAAT;
        delete woonhuisData.Contractdocument.PP.BA.BA_PLAATS;
        saveStepData('woonhuis', woonhuisData);
      }
    }

    // Sync to Aansprakelijkheid if it exists
    if (selectedProducts?.aansprakelijkheid) {
      const avpData = getStepData('aansprakelijkheid');
      if (avpData) {
        if (!avpData.Contractdocument) avpData.Contractdocument = {};
        if (!avpData.Contractdocument.PP) avpData.Contractdocument.PP = {};
        if (!avpData.Contractdocument.PP.BA) avpData.Contractdocument.PP.BA = {};
        avpData.Contractdocument.PP.BA.BA_HUISNR = value;
        // Clear stale street/city in synced product
        delete avpData.Contractdocument.PP.BA.BA_STRAAT;
        delete avpData.Contractdocument.PP.BA.BA_PLAATS;
        saveStepData('aansprakelijkheid', avpData);
      }
    }

    // Note: Address lookup is now triggered explicitly on blur/enter, not on every field change
    // This prevents premature API calls while user is still typing
  }
  // Handle BA_TOEVOEG - updates BA_TOEVOEG, and VP_TOEVOEG only when J
  else if (fieldPath === 'Contractdocument.PP.BA.BA_TOEVOEG') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_TOEVOEG: value
          },
          ...(risicoAdresGelijk === 'J' ? {
            VP: {
              ...prevData.Contractdocument?.PP?.VP,
              VP_TOEVOEG: value
            }
          } : {})
        }
      }
    };

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_TOEVOEG = value;
        if (risicoAdresGelijk === 'J') {
          woonhuisData.Contractdocument.PP.VP.VP_TOEVOEG = value;
        }
        saveStepData('woonhuis', woonhuisData);
      }
    }

    // Sync to Aansprakelijkheid if it exists
    if (selectedProducts?.aansprakelijkheid) {
      const avpData = getStepData('aansprakelijkheid');
      if (avpData) {
        if (!avpData.Contractdocument) avpData.Contractdocument = {};
        if (!avpData.Contractdocument.PP) avpData.Contractdocument.PP = {};
        if (!avpData.Contractdocument.PP.BA) avpData.Contractdocument.PP.BA = {};
        avpData.Contractdocument.PP.BA.BA_TOEVOEG = value;
        saveStepData('aansprakelijkheid', avpData);
      }
    }

  }
  // Handle BA_STRAAT - updates BA_STRAAT, and VP_STRAAT only when J
  else if (fieldPath === 'Contractdocument.PP.BA.BA_STRAAT') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_STRAAT: value
          },
          ...(risicoAdresGelijk === 'J' ? {
            VP: {
              ...prevData.Contractdocument?.PP?.VP,
              VP_STRAAT: value
            }
          } : {})
        }
      }
    };

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_STRAAT = value;
        woonhuisData.Contractdocument.PP.VP.VP_STRAAT = value;
        saveStepData('woonhuis', woonhuisData);
      }
    }

    // Sync to Aansprakelijkheid if it exists
    if (selectedProducts?.aansprakelijkheid) {
      const avpData = getStepData('aansprakelijkheid');
      if (avpData) {
        if (!avpData.Contractdocument) avpData.Contractdocument = {};
        if (!avpData.Contractdocument.PP) avpData.Contractdocument.PP = {};
        if (!avpData.Contractdocument.PP.BA) avpData.Contractdocument.PP.BA = {};
        avpData.Contractdocument.PP.BA.BA_STRAAT = value;
        saveStepData('aansprakelijkheid', avpData);
      }
    }

  }
  // Handle BA_PLAATS - updates BA_PLAATS, and VP_PLAATS only when J
  else if (fieldPath === 'Contractdocument.PP.BA.BA_PLAATS') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_PLAATS: value
          },
          ...(risicoAdresGelijk === 'J' ? {
            VP: {
              ...prevData.Contractdocument?.PP?.VP,
              VP_PLAATS: value
            }
          } : {})
        }
      }
    };

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_PLAATS = value;
        woonhuisData.Contractdocument.PP.VP.VP_PLAATS = value;
        saveStepData('woonhuis', woonhuisData);
      }
    }

    // Sync to Aansprakelijkheid if it exists
    if (selectedProducts?.aansprakelijkheid) {
      const avpData = getStepData('aansprakelijkheid');
      if (avpData) {
        if (!avpData.Contractdocument) avpData.Contractdocument = {};
        if (!avpData.Contractdocument.PP) avpData.Contractdocument.PP = {};
        if (!avpData.Contractdocument.PP.BA) avpData.Contractdocument.PP.BA = {};
        avpData.Contractdocument.PP.BA.BA_PLAATS = value;
        saveStepData('aansprakelijkheid', avpData);
      }
    }

  }
  // Handle IB_INBVST (Vaststelling inboedelwaarde) - derives BA_HERBVST, IP_GARONDV, IP_TAXVRT
  else if (fieldPath === 'Contractdocument.PP.IB.IB_INBVST') {
    // IP_GARONDV: 'M' (Waardebepaling maatschappij) -> 'J', others -> 'N'
    const garondv = value === 'M' ? 'J' : 'N';
    // IP_TAXVRT: 'V' (Vaste taxatie) -> 'J', others -> 'N'
    const taxvrt = value === 'V' ? 'J' : 'N';
    // BA_HERBVST: inboedel schema only allows 'E' and 'V'
    // Map: 'E' -> 'E', 'V' -> 'V', 'M' -> 'E' (closest valid option)
    const herbvst = value || 'E';
    // Clear taxatie sub-fields when not 'V' (Vaste taxatie) — mirrors Woonhuis
    const baUpdate: any = { ...prevData.Contractdocument?.PP?.BA, BA_HERBVST: herbvst };
    if (value !== 'V') {
      delete baUpdate.BA_EINDGTX;
    }

    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          IB: {
            ...prevData.Contractdocument?.PP?.IB,
            IB_INBVST: value
          },
          BA: baUpdate,
          IP: {
            ...prevData.Contractdocument?.PP?.IP,
            IP_GARONDV: garondv,
            IP_TAXVRT: taxvrt
          }
        }
      },
      Supportsection: {
        ...prevData.Supportsection,
        Divers: {
          ...prevData.Supportsection?.Divers,
          ...(value !== 'V' ? { TaxatieVRT: undefined } : {})
        }
      }
    };
  }
  // Handle BA_EINDGTX (Einddatum geldigheid taxatie)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_EINDGTX') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_EINDGTX: value
          }
        }
      }
    };
  }
  // Handle Supportsection.Divers.TaxatieVRT (Taxatie door VRT-taxateur)
  else if (fieldPath === 'Supportsection.Divers.TaxatieVRT') {
    newData = {
      ...prevData,
      Supportsection: {
        ...prevData.Supportsection,
        Divers: {
          ...prevData.Supportsection?.Divers,
          TaxatieVRT: value
        }
      }
    };
  }
  // Handle IP_GARONDV (Garantie tegen onderverzekering - Rhion R515)
  else if (fieldPath === 'Contractdocument.PP.IP.IP_GARONDV') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          IP: {
            ...prevData.Contractdocument?.PP?.IP,
            IP_GARONDV: value
          }
        }
      }
    };
  }
  // Handle IP_GSS (Gezinssamenstelling) - sync to Woonhuis and Aansprakelijkheid
  else if (fieldPath === 'Contractdocument.PP.IP.IP_GSS') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          IP: {
            ...prevData.Contractdocument?.PP?.IP,
            IP_GSS: value
          }
        }
      }
    };

    // Persist IP_GSS immediately so it survives navigation
    saveWorkflowData(screenId, newData);

    // Family composition is picked once and shared with every other selected product,
    // regardless of workflow order (not just Woonhuis + Aansprakelijkheid).
    syncFamilyCompositionToOtherProducts(screenId, value, selectedProducts, getStepData, saveStepData);
  }
  // Handle BA_TYPEWON (Soort woning) - sync to Woonhuis
  else if (fieldPath === 'Contractdocument.PP.BA.BA_TYPEWON') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_TYPEWON: value
          }
        }
      }
    };

    // Sync to Woonhuis if it exists. Inboedel-only codes ('A' appartement, '6' maisonnette) are
    // declined by Woonhuis_2024, so Woonhuis gets its 'U' equivalent instead.
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_TYPEWON = toWoonhuisTypeWoning(value) ?? value;
        saveStepData('woonhuis', woonhuisData);
      }
    }
  }
  // Handle BA_WONING (Gebruik pand) - sync to Woonhuis and guard VR coverage
  else if (fieldPath === 'Contractdocument.PP.BA.BA_WONING') {
    const isRentalValue = ['B', 'C', 'D', 'H'].includes(value);

    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_WONING: value
          },
          // Reset VR when switching to non-rental property
          ...(!isRentalValue && { VR: {} })
        }
      },
      // Auto-disable Verhuur coverage when non-rental
      ...(!isRentalValue && {
        Supportsection: {
          ...prevData.Supportsection,
          Dekkingen: {
            ...prevData.Supportsection?.Dekkingen,
            Verhuur: 'N'
          }
        }
      })
    };

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_WONING = value;
        saveStepData('woonhuis', woonhuisData);
      }
    }
  }
  // Handle BA_OPPWON (Oppervlakte) - Inboedel-specific, no sync
  else if (fieldPath === 'Contractdocument.PP.BA.BA_OPPWON') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_OPPWON: value
          }
        }
      }
    };
    // Note: Not synced to Woonhuis as this is Inboedel-specific
  }
  // Handle BA_AARDBW (Bouwaard muren) - sync to Woonhuis
  else if (fieldPath === 'Contractdocument.PP.BA.BA_AARDBW') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_AARDBW: value
          }
        }
      }
    };

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_AARDBW = value;
        saveStepData('woonhuis', woonhuisData);
      }
    }
  }
  // Handle BA_AARDDAK (Bouwaard dak) - sync to Woonhuis
  else if (fieldPath === 'Contractdocument.PP.BA.BA_AARDDAK') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_AARDDAK: value
          }
        }
      }
    };

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_AARDDAK = value;
        saveStepData('woonhuis', woonhuisData);
      }
    }
  }
  // Handle BA_EIGENWO (Eigen woning) - Rhion R515 specific
  else if (fieldPath === 'Contractdocument.PP.BA.BA_EIGENWO') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_EIGENWO: value
          }
        }
      }
    };
  }
  // Handle BA_PERMBEW (Permanent bewoner) - Rhion R515 specific
  else if (fieldPath === 'Contractdocument.PP.BA.BA_PERMBEW') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_PERMBEW: value
          }
        }
      }
    };
  }
  // Handle BA_MONUMEN (Monumentaal pand)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_MONUMEN') {
    const isMonument = value === 'J';
    setIsMonumentProperty(isMonument);
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_MONUMEN: value
          }
        }
      }
    };

    // Sync to Woonhuis if it exists
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        woonhuisData.Contractdocument.PP.BA.BA_MONUMEN = value;
        // Clear BA_SRTMON on woonhuis when monument is set to 'N'
        if (!isMonument && woonhuisData.Contractdocument.PP.BA.BA_SRTMON) {
          delete woonhuisData.Contractdocument.PP.BA.BA_SRTMON;
        }
        saveStepData('woonhuis', woonhuisData);
      }
    }
  }
  // Handle BA_BDRFBEL (Belendend gebouw) - Rhion R515 specific
  else if (fieldPath === 'Contractdocument.PP.BA.BA_BDRFBEL') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_BDRFBEL: value
          }
        }
      }
    };
  }
  else if (fieldPath === 'Contractdocument.PP.BA.BA_HERBWRD') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_HERBWRD: value
          }
        }
      }
    };
  }
  // Handle all other fields generically
  else {
    const fieldParts = fieldPath.split('.');
    let current = newData;

    for (let i = 0; i < fieldParts.length - 1; i++) {
      if (!current[fieldParts[i]]) {
        current[fieldParts[i]] = {};
      }
      current = current[fieldParts[i]];
    }

    current[fieldParts[fieldParts.length - 1]] = value;
  }

  // Persist coverage toggles immediately so selections survive navigation/refresh.
  if (fieldPath.startsWith('Supportsection.Dekkingen.')) {
    setTimeout(() => saveStepData(screenId, newData), 0);
  }

  return newData;
};
